import React, { useMemo, useState } from 'react';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { WriteBackToGreenhousePlaceToWrite } from 'libs/end-to-end/src/types/databases/workflows/strategies/greenhouse-write-back';

export interface ConfigureWriteBackGreenhousePlacesToWriteProps
  extends ConfigureWorkflowBaseProps {
  placesToWrite: WriteBackToGreenhousePlaceToWrite[];

  updatePlacesToWrite: (
    placesToWrite: WriteBackToGreenhousePlaceToWrite[],
  ) => void;
}

export const ConfigureWriteBackGreenhousePlacesToWrite = ({
  placesToWrite,
  updatePlacesToWrite,
}: ConfigureWriteBackGreenhousePlacesToWriteProps) => {
  const [newWritePlace, setNewWritePlace] =
    useState<WriteBackToGreenhousePlaceToWrite | null>(null);
  const addBehavior: TouchTargetBehavior = useMemo(() => {
    return {
      type: 'onClick',
      onClick: () => {
        if (!newWritePlace) {
          return;
        }
        updatePlacesToWrite([...placesToWrite, newWritePlace]);
      },
    };
  }, [newWritePlace, placesToWrite, updatePlacesToWrite]);
  const placeTypeList = useMemo(() => {
    return [
      { optionLabel: 'None', optionValue: 'none' },
      { optionLabel: 'Custom Field', optionValue: 'customField' },
    ];
  }, []);
  return (
    <div className="space-y-4 p-4 bg-white rounded shadow">
      <label className="block text-sm font-medium text-gray-700">
        Select a place
        <select
          className="form-select mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          value={newWritePlace?.type ?? 'none'}
          onChange={(e) => {
            const newSelected = e.target.value as
              | 'none'
              | WriteBackToGreenhousePlaceToWrite['type'];
            switch (newSelected) {
              case 'customField':
                setNewWritePlace({
                  type: 'customField',
                  customFieldKey: '',
                  onBehalfOfUserId: '',
                });
                break;
              default:
                setNewWritePlace(null);
            }
          }}
        >
          {placeTypeList.map((place) => (
            <option key={place.optionValue} value={place.optionValue}>
              {place.optionLabel}
            </option>
          ))}
        </select>
      </label>
      {newWritePlace && newWritePlace.type === 'customField' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Custom Field Key
              <input
                className="form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                type="text"
                value={newWritePlace.customFieldKey ?? ''}
                onChange={(e) =>
                  setNewWritePlace({
                    ...newWritePlace,
                    customFieldKey: e.target.value,
                  })
                }
              />
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              On Behalf Of User ID
              <input
                className="form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                type="text"
                value={newWritePlace.onBehalfOfUserId ?? ''}
                onChange={(e) =>
                  setNewWritePlace({
                    ...newWritePlace,
                    onBehalfOfUserId: e.target.value,
                  })
                }
              />
            </label>
          </div>
        </>
      )}
      <Button behavior={addBehavior} disabled={!newWritePlace} className="mt-4">
        Add
      </Button>
      <Table
        items={placesToWrite}
        columnDefinitions={[
          {
            id: 'place',
            header: () => <div>Place</div>,
            cell: ({ item }) => JSON.stringify(item),
          },
          {
            id: 'delete',
            header: () => <div>Delete</div>,
            cell: ({ rowIndex }) => {
              return (
                <Button
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      const newPlaces = [...placesToWrite];
                      newPlaces.splice(rowIndex, 1);
                      updatePlacesToWrite(newPlaces);
                    },
                  }}
                  variant={'outline'}
                  size={'small'}
                >
                  Delete
                </Button>
              );
            },
          },
        ]}
      />
    </div>
  );
};
